import { Button, Fab } from '@/components/elements/forms/buttons';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import FakeSearchInput from '@/components/modules/mobile/FakeSearchInput';
import { classnames } from '@/helpers';
import { SearchManagerOutput } from '@/hooks/search/useSearch';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { autocompleteService } from '@/services';
import { FocusEvent, Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import KeywordAutoSuggest from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest';
import { KeywordAutoSuggestManagerOutput } from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.type';
import LocationAutosuggest from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest';
import {
  LocationAutoSuggestManagerOutput,
  LocationSuggestion,
} from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.types';
import {
  AutoSuggestInputComponent,
  KeywordAutoSuggestSectionTitle,
  KeywordSuggestionItem,
  LocationAutoSuggestSectionTitle,
  LocationSuggestionItem,
} from '../../components/AutoSuggest/shared/templates';
import { AutoSuggestInputTheme } from '../../components/AutoSuggest/shared/types';
import DateTimePicker from '../../components/DateTimePicker/DateTimePicker';
import { getDateTimeFilterLabel } from '../../components/DateTimePicker/DateTimePicker.helpers';
import { DateTimePickerManagerOutput } from '../../components/DateTimePicker/DateTimePicker.types';
import { getSearchPlaceholderText } from './SearchForm.helpers';

type SearchFormProps = {
  searchManager: SearchManagerOutput;
} & {
  keywordAutoSuggestManager: KeywordAutoSuggestManagerOutput;
} & {
  locationAutoSuggestManager: LocationAutoSuggestManagerOutput;
} & {
  dateTimePickerManager: DateTimePickerManagerOutput;
};

export type FocusInput = 'keyword' | 'location';

const baseTranslationKey = 'features.searchV2.modules.SearchForm';

const SUGGESTION_INPUT_ID = {
  KEYWORD: 'autosuggest-keyword-input',
  LOCATION: 'autosuggest-location-input',
};

const getSearchFormGridTemplateColumnsStyles = (isSistaminutenSearch: boolean) => {
  const style: React.CSSProperties = {};

  if (isSistaminutenSearch) {
    style.gridTemplateColumns = '1fr 1fr';
  } else {
    style.gridTemplateColumns = '1.2fr 1fr 1fr';
  }
  return style;
};

const AUTOGUSGEST_THEME: AutoSuggestInputTheme = {
  container: 'relative w-full',
  suggestionsContainer: 'absolute w-full right-0 left-0 max-h-[550px]',
  suggestion: 'cursor-pointer hover:bg-surface_l4 rounded-md space-x-md',
  suggestionHighlighted: 'bg-surface_l4 rounded-md',
  suggestionsList: 'space-y-xs',
  suggestionsContainerOpen:
    'z-[1001] bg-white border border-border_bold bg-white p-xl mt-xs rounded-md max-h-[550px] overflow-y-auto',
};

const SearchForm = (props: SearchFormProps) => {
  const keywordInputRef = useRef(null);
  const keywordSuggestionsRef = useRef(null);
  const locationSuggestionsRef = useRef(null);
  const dateTimePickerContainerRef = useRef(null);
  const locationInputRef = useRef(null);
  const [isFocused, setIsFocused] = useState<FocusInput>(undefined);
  const { searchManager, keywordAutoSuggestManager, locationAutoSuggestManager, dateTimePickerManager } = props;
  const isSistaminutenSearch = searchManager.type === 'sistaminuten';

  function handleOnClearKeyword() {
    keywordInputRef?.current?.focus?.();
    keywordAutoSuggestManager.onChange(null, { newValue: '' });
    searchManager.performSearch(
      { q: '', location: locationAutoSuggestManager.location },
      { searchContext: 'free_search' },
    );
  }

  function handleClearLocation() {
    locationInputRef?.current?.focus?.();
    locationAutoSuggestManager.onChange({ target: { value: '' } });
    searchManager.performSearch({ q: keywordAutoSuggestManager.keyword, location: '' });
  }

  function handleOnSearchClick() {
    const { keyword } = keywordAutoSuggestManager;
    const { location } = locationAutoSuggestManager;
    const { startDate, endDate, timeOfDay } = dateTimePickerManager;
    const { lat, lon } = searchManager;

    searchManager.performSearch(
      {
        q: keyword,
        location,
        startDate,
        endDate,
        timeOfDay,
        lat,
        lon,
      },
      { searchContext: 'free_search' },
    );
  }

  function handleOnAutoSuggestKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter' || event.keyCode === 13) {
      handleOnSearchClick();
    }
  }

  function handleOnFocus(type: FocusInput, cb: () => void) {
    setIsFocused(type);
    dateTimePickerManager.onCloseCalendar();
    cb?.();
  }

  function handleOnBlur(e: FocusEvent<HTMLInputElement>, cb: () => void) {
    setIsFocused(undefined);
    cb?.();
  }

  function handleRemoveLocationHistorySuggestion(suggestion: LocationSuggestion) {
    autocompleteService.deleteLocationSuggestion(suggestion.label);
    locationAutoSuggestManager.onClearHistorySuggestion(suggestion);
  }

  const inputSharedClassNames = classnames(
    'border-border_regular group-hover:border-border_bold group-hover:bg-surface_l4 h-[52px] transition-colors overflow-hidden text-ellipsis',
    isFocused || dateTimePickerManager.showCalendar ? 'bg-surface_l4' : 'bg-surface_l2',
  );

  /**
   * Only show search button when either keyword, location or time filters input are not matching the url
   */
  const isSyncedKeywordInputWithCurrentSearch = keywordAutoSuggestManager.keyword === searchManager.q;
  const isSyncedLocationInputWithCurrentSearch = locationAutoSuggestManager.location === searchManager.location;
  const isSyncedDateTimePickerWithCurrentSearch =
    dateTimePickerManager.startDate === searchManager.startDate &&
    dateTimePickerManager.endDate === searchManager.endDate &&
    dateTimePickerManager.timeOfDay === searchManager.timeOfDay;

  const isSyncedWithCurrentSearch =
    isSyncedKeywordInputWithCurrentSearch &&
    isSyncedLocationInputWithCurrentSearch &&
    isSyncedDateTimePickerWithCurrentSearch;

  return (
    <div className="flex flex-col">
      <div className={classnames('hover:shadow-xs group flex items-stretch rounded-full', isFocused && 'shadow-xs')}>
        <div
          className={classnames('relative grid w-full')}
          style={getSearchFormGridTemplateColumnsStyles(isSistaminutenSearch)}>
          <div className="relative">
            <KeywordAutoSuggest
              inputRef={keywordInputRef}
              {...keywordAutoSuggestManager}
              onKeyDown={handleOnAutoSuggestKeyDown}
              theme={AUTOGUSGEST_THEME}
              renderInputComponent={({ key, ...inputProps }) => (
                <AutoSuggestInputComponent
                  key={key}
                  inputProps={{
                    ...inputProps,
                    id: SUGGESTION_INPUT_ID.KEYWORD,
                    onFocus: () => handleOnFocus('keyword', inputProps.onFocus),
                    onBlur: (e) => handleOnBlur(e, inputProps.onBlur),

                    /**
                     * Change placeholder with title when input is focused
                     */
                    placeholder: !isFocused ? inputProps.title : inputProps.placeholder,
                  }}
                  isFocused={isFocused === 'keyword'}
                  onClear={handleOnClearKeyword}
                  leftSlot={<Icon variant="search" size="sm" />}
                  className={twMerge(
                    classnames(
                      inputSharedClassNames,
                      'border-border_regular rounded-bl-full rounded-tl-full border-r-0',
                    ),
                  )}
                />
              )}
              renderSuggestion={(suggestion, options) => (
                <KeywordSuggestionItem suggestion={suggestion} options={options} />
              )}
              renderSectionTitle={(section) => <KeywordAutoSuggestSectionTitle {...section} />}
              renderSuggestionsContainer={({ containerProps, children }) => {
                return keywordSuggestionsRef.current
                  ? createPortal(<div {...containerProps}>{children}</div>, keywordSuggestionsRef.current)
                  : null;
              }}
            />

            {/* Seperator */}
            {!isFocused && !dateTimePickerManager.showCalendar && (
              <div className="bg-border_regular bottom-xs top-xs absolute right-0 w-[1px]"></div>
            )}
          </div>

          <div className="relative">
            <LocationAutosuggest
              inputRef={locationInputRef}
              {...locationAutoSuggestManager}
              onKeyDown={(event) => handleOnAutoSuggestKeyDown(event)}
              theme={AUTOGUSGEST_THEME}
              renderInputComponent={({ key, ...inputProps }) => (
                <AutoSuggestInputComponent
                  key={key}
                  inputProps={{
                    ...inputProps,
                    id: SUGGESTION_INPUT_ID.LOCATION,
                    onFocus: () => handleOnFocus('location', inputProps.onFocus),
                    onBlur: (e) => handleOnBlur(e, inputProps.onBlur),
                  }}
                  isFocused={isFocused === 'location'}
                  onClear={handleClearLocation}
                  leftSlot={
                    locationAutoSuggestManager.isFetchingUserPosition ? (
                      <LoadingIcon />
                    ) : (
                      <Icon variant="location" size="sm" />
                    )
                  }
                  className={classnames(inputSharedClassNames, 'border-l-0 border-r-0')}
                />
              )}
              renderSuggestion={(suggestion, options) => (
                <LocationSuggestionItem
                  suggestion={suggestion}
                  options={options}
                  onRemoveHistorySuggestion={handleRemoveLocationHistorySuggestion}
                />
              )}
              renderSectionTitle={(section) => <LocationAutoSuggestSectionTitle {...section} />}
              renderSuggestionsContainer={({ containerProps, children }) => {
                return locationSuggestionsRef.current
                  ? createPortal(<div {...containerProps}>{children}</div>, locationSuggestionsRef.current)
                  : null;
              }}
            />
            {/* Seperator */}
            {!isSistaminutenSearch && !isFocused && !dateTimePickerManager.showCalendar && (
              <div className="bg-border_regular bottom-xs top-xs absolute right-0 w-[1px]"></div>
            )}
          </div>

          {!isSistaminutenSearch && (
            <DateTimePicker
              {...dateTimePickerManager}
              renderCalendarContainer={({ children }) => {
                return dateTimePickerContainerRef.current
                  ? createPortal(
                      <div className="-bottom-xs absolute w-full">{children}</div>,
                      dateTimePickerContainerRef.current,
                    )
                  : null;
              }}
              renderInputComponent={(inputProps) => {
                const { hasFilterApplied, ...props } = inputProps;
                return (
                  <Fragment>
                    <div
                      className={classnames(
                        inputSharedClassNames,
                        'px-md relative flex items-center border border-l-0 border-r-0',
                      )}>
                      <span className="pointer-events-none z-10">
                        <Icon variant="calendar-event" />
                      </span>
                      <div className="py-xs space-y-3xs z-10 flex w-full flex-col">
                        {dateTimePickerManager.showCalendar && (
                          <span className="ml-sm text-xs">{props.placeholder}</span>
                        )}
                        <input
                          {...props}
                          className={classnames(
                            props.className,
                            'z-10 cursor-pointer overflow-hidden text-ellipsis',
                            // padding to make text ellipsis show when clear button is visible
                            hasFilterApplied && 'pr-2xl',
                          )}
                        />
                      </div>
                      {hasFilterApplied && (
                        <button
                          onClick={dateTimePickerManager.onClearDateTime}
                          onMouseDown={dateTimePickerManager.onClearDateTime}
                          className={classnames(
                            'bg-bg_secondary right-sm absolute top-1/2 z-10 flex h-[32px] w-[32px] -translate-y-1/2 items-center justify-center rounded-full',
                          )}>
                          <Icon variant="close-alt" />
                        </button>
                      )}
                    </div>
                    <div
                      className={classnames(
                        '-z-1 pointer-events-none absolute inset-[1px] rounded-full',
                        dateTimePickerManager.showCalendar && '!bg-bg_neutral_alwaysLight drop-shadow-xs',
                      )}></div>
                  </Fragment>
                );
              }}
            />
          )}
        </div>

        <div
          className={classnames(
            inputSharedClassNames,
            'pl-xs pr-xs flex h-auto items-center justify-center rounded-br-full rounded-tr-full border border-l-0',
          )}>
          {!isSyncedWithCurrentSearch && (
            <Button
              className="flex-shrink-0 rounded-[1000px]"
              leftIcon={<Icon variant="search" size="xs" />}
              variant="primary"
              size="sm"
              {...(dateTimePickerManager.showCalendar && { tabIndex: -1 })}
              onClick={handleOnSearchClick}>
              {_s('search')}
            </Button>
          )}
          {isSyncedWithCurrentSearch && (
            <Fab
              onClick={handleOnSearchClick}
              size="sm"
              style={{ width: 40, height: 40 }}
              className="flex-shrink-0"
              icon={<Icon variant="search" size="xs" style={{ width: 16, height: 16, filter: 'invert(1)' }} />}
            />
          )}
        </div>
      </div>
      <div className="grid" style={getSearchFormGridTemplateColumnsStyles(isSistaminutenSearch)}>
        {/* Keyword Suggestions container */}
        <div className="relative col-span-2" ref={keywordSuggestionsRef}></div>
        {/* Location Suggestions container */}
        <div className="relative col-span-2 col-start-2" ref={locationSuggestionsRef}></div>
        {/* Date time picker container */}
        <div className="col-start-0 relative col-span-3 col-start-1" ref={dateTimePickerContainerRef}></div>
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function (props: SearchFormProps) {
  const history = useHistory();
  const location = useLocation();
  const { isMobileView } = useMobileView();
  const isSistaminutenSearch = props.searchManager.type === 'sistaminuten';

  function handleOnFakeSearchInputClick() {
    history.push({
      pathname: '/search',
      search: location.search,
      state: {
        q: props.searchManager.q,
        location: props.searchManager.location,
        isSistaminutenSearch,
      },
    });
  }

  function handleOnClearFakeSearchInputClick() {
    history.push({
      pathname: '/search',
      search: location.search,
      state: {
        q: undefined,
        location: props.searchManager.location,
        isSistaminutenSearch,
      },
    });
  }

  if (isMobileView) {
    return (
      <FakeSearchInput
        isLoading={false}
        onClick={handleOnFakeSearchInputClick}
        valid={Boolean(props.searchManager.q || props.searchManager.location)}
        icon="search"
        className="bg-bg_surface_l2"
        onClear={handleOnClearFakeSearchInputClick}
        content={<FakeSearchInputContent {...props} />}
        size="sm"
      />
    );
  }

  return <SearchForm {...props} />;
}

function FakeSearchInputContent(props: SearchFormProps) {
  const { keyword, location } = getSearchPlaceholderText(props.searchManager);

  if (!props.searchManager.q && !props.searchManager.location) {
    return <span className="text-fg_primary text-m">{_s('features.searchV2.shared.whatDoYouWantToBook')}</span>;
  }

  const selectedDateRange = props.searchManager.startDate
    ? getDateTimeFilterLabel(
        props.dateTimePickerManager.startDate,
        props.dateTimePickerManager.endDate,
        props.dateTimePickerManager.timeOfDay,
      )
    : undefined;

  return (
    <Fragment>
      <span className={classnames('text-m text-fg_primary block', (location || selectedDateRange) && 'mb-3xs')}>
        {keyword}
      </span>
      <span className="text-fg_secondary block w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs">
        {selectedDateRange && <Fragment>{selectedDateRange} &middot; </Fragment>}
        {location}
      </span>
    </Fragment>
  );
}
